import { PROPOSAL_ORDER, PROPOSAL_STATUS, TYPE_LABEL } from "@/lib/sk";
import { TYPE_OPTIONS, FAIL_OPTIONS, APPLICATION_OPTIONS, cellText } from "./model";

export const FILTERS_KEY = (cid) => `sk.ventiler.filters.${cid}`;

/** Combinable facets. `of` gives the valve's value(s) in the dimension; a valve matches a dimension when any of its values is picked. */
export const FILTER_DIMS = [
  { key: "status", label: "Teknisk status", options: PROPOSAL_ORDER.map((s) => [s, PROPOSAL_STATUS[s].label]), of: (v) => v.proposal?.status },
  { key: "why", label: "Årsak", dynamic: true, of: (v) => v.proposal?.next_step?.why_key, labelOf: (v) => v.proposal?.next_step?.why_label },
  { key: "origin", label: "Opprinnelse", options: [["system", "Systemets forslag"], ["valgt", "Eget valg"], ["ingen", "Uten aktuator"]], of: (v) => (v.selection ? "valgt" : v.proposal?.code ? "system" : "ingen") },
  { key: "type", label: "Ventiltype", options: TYPE_OPTIONS, of: (v) => v.type },
  { key: "fail", label: "Feilmodus", options: FAIL_OPTIONS, of: (v) => v.fail_mode },
  { key: "application", label: "Funksjon", options: APPLICATION_OPTIONS, of: (v) => v.application },
  { key: "data", label: "Kundens data", options: [["avklares", "Må avklares"], ["endret", "Endret av dere"], ["komplett", "Komplett"]], of: (v) => [v.status !== "mapped" ? "avklares" : "komplett", Object.keys(v.overrides || {}).length ? "endret" : null].filter(Boolean) },
  { key: "system", label: "System", dynamic: true, of: (v) => v.system || null, labelOf: (v) => v.system },
];

export const activeCount = (f) => Object.values(f || {}).reduce((n, a) => n + (a?.length || 0), 0);

const has = (dim, v, picked) => { const val = dim.of(v); const vals = Array.isArray(val) ? val : [val]; return vals.some((x) => picked.includes(x)); };

export function matchSearch(v, q) {
  if (!q) return true;
  const p = v.proposal || {};
  const s = q.toLowerCase();
  return [v.tag, cellText("type", v.type), v.valve_model, v.valve_maker, v.pid, v.system, v.zone, v.remarks, v.esd_group, p.code, p.family, v.flange, p.next_step?.label, p.next_step?.why_label, v.selection?.by, v.dn && `dn${v.dn}`, v.dn && `dn ${v.dn}`].some((x) => String(x || "").toLowerCase().includes(s));
}

export function matchFilters(v, f, q) {
  for (const dim of FILTER_DIMS) { const picked = f?.[dim.key]; if (picked?.length && !has(dim, v, picked)) return false; }
  return matchSearch(v, q);
}

/** Options for a dynamic dimension (årsak, system) from the data itself – nothing is listed that no valve has. */
export function dynamicOptions(valves, dim) {
  const map = new Map();
  valves.forEach((v) => { const k = dim.of(v); if (k) map.set(k, dim.labelOf(v) || k); });
  return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], "nb"));
}

/** Faceted counts: for every option, how many valves would match if that option were (also) picked – given the other dimensions and the search. */
export function facetCounts(valves, f, q) {
  const out = {};
  FILTER_DIMS.forEach((dim) => {
    const others = valves.filter((v) => matchSearch(v, q) && FILTER_DIMS.every((d) => d.key === dim.key || !f?.[d.key]?.length || has(d, v, f[d.key])));
    const counts = {};
    others.forEach((v) => { const val = dim.of(v); (Array.isArray(val) ? val : [val]).forEach((x) => { if (x) counts[x] = (counts[x] || 0) + 1; }); });
    out[dim.key] = counts;
  });
  return out;
}

export function toggleFilter(f, dim, value) {
  const cur = f?.[dim] || [];
  const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
  const out = { ...(f || {}) };
  if (next.length) out[dim] = next; else delete out[dim];
  return out;
}

export function optionLabel(dim, value, valves) {
  const d = FILTER_DIMS.find((x) => x.key === dim);
  if (!d) return value;
  if (d.dynamic) return dynamicOptions(valves, d).find(([k]) => k === value)?.[1] || value;
  return d.options.find(([k]) => k === value)?.[1] || (dim === "type" ? TYPE_LABEL[value] : value) || value;
}

/** What the Konfigurasjon tab and the verdict cards still send: one status word, «avklares», «endret», «valgt» or «alle». */
export function legacyFilter(key) {
  if (!key || key === "alle") return {};
  if (PROPOSAL_ORDER.includes(key)) return { status: [key] };
  if (key === "avklares" || key === "endret") return { data: [key] };
  if (key === "valgt") return { origin: ["valgt"] };
  return null;
}
